import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Typography, Tooltip } from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";

const NotificationPermissionToggle: React.FC = () => {
  const [permission, setPermission] = useState<NotificationPermission | null>(null);

  useEffect(() => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const handleEnable = async () => {
    if (!("Notification" in window)) return;

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
    } catch (err) {
      console.error("Error requesting notification permission:", err);
    }
  };

  if (permission === null) return null; // not yet checked

  let icon, statusText, button;

  switch (permission) {
    case "granted":
      icon = <NotificationsActiveIcon color="success" fontSize="small" />;
      statusText = "Enabled";
      button = (
        <Tooltip title="Notifications are already enabled in your browser">
          <span>
            <Button size="small" variant="outlined" disabled>
              Enable
            </Button>
          </span>
        </Tooltip>
      );
      break;

    case "default":
      icon = <NotificationsOffIcon color="action" fontSize="small" />;
      statusText = "Disabled";
      button = (
        <Button size="small" variant="outlined" onClick={handleEnable}>
          Enable
        </Button>
      );
      break;

    case "denied":
      icon = <NotificationsOffIcon color="error" fontSize="small" />;
      statusText = "Blocked";
      button = (
        <Tooltip title="Notifications are blocked. Change browser settings to enable.">
          <span>
            <Button size="small" variant="outlined" disabled>
              Blocked
            </Button>
          </span>
        </Tooltip>
      );
      break;
  }

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        borderRadius: 1,
        bgcolor: "action.hover",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon}
        <Typography variant="body2" color="text.secondary">
          Notifications are {statusText}
        </Typography>
      </Stack>
      {button}
    </Box>
  );
};

export default NotificationPermissionToggle;
